import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { AppState } from '@nxsys/store';
import { Assignment } from '@nxsys/store/slices/assignment/types';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetAssignmentDashboardListParams } from '@nxsys/store/slices/dashboard/types';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export enum AssignmentModalTypeEnum {
  ACTIVE = 'ACTIVE',
  NEARING_COMPLETION = 'NEARING_COMPLETION',
  PLACEMENTS = 'PLACEMENTS',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: AssignmentModalTypeEnum;
};

const AssignmentModal: React.FC<Props> = ({ isOpen, toggle, type }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { assignmentList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = assignmentList;

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetAssignmentDashboardListParams) => {
    const newParams = {
      ...params,
    };
    if (type === AssignmentModalTypeEnum.ACTIVE) {
      newParams.isActiveOnly = true;
      dispatch(dashboardActions.getAssignmentDashboardList(newParams));
    } else if (type === AssignmentModalTypeEnum.NEARING_COMPLETION) {
      newParams.isNearingCompletion = true;
      dispatch(dashboardActions.getAssignmentDashboardList(newParams));
    } else if (type === AssignmentModalTypeEnum.PLACEMENTS) {
      newParams.notFinished = true;
      dispatch(dashboardActions.getPlacementDashboardList(newParams));
    }
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<Assignment> = [
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
      width: 150,
    },
    {
      key: 'name',
      title: 'Assignment Name',
      width: 250,
      dataIndex: 'assignmentName',
    },
    {
      key: 'empee_sub',
      title: messages['common.employee'],
      width: 250,
      dataIndex: 'subcontractorRef',
      render: (value) => `${value?.forename || ''} ${value?.surname || ''}`,
    },
    {
      key: 'agency_name',
      title: 'Agency Name',
      dataIndex: 'agencyRef',
      width: 300,
      render: (value) => value?.name || '',
    },
    {
      key: 'startDate',
      title: 'Start Date',
      dataIndex: 'startDate',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value && moment(value) ? moment(value)?.format('DD/MM/YYYY') : '_',
    },
    {
      key: 'endDate',
      title: 'End Date',
      dataIndex: 'endDate',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value && moment(value) ? moment(value)?.format('DD/MM/YYYY') : '_',
    },
  ];

  return (
    <AppModalContainer
      title={
        type === AssignmentModalTypeEnum.ACTIVE
          ? 'Active Assignments'
          : type === AssignmentModalTypeEnum.NEARING_COMPLETION
            ? 'Nearing Completion Assignments'
            : 'Placements'
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1500 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default AssignmentModal;
